import React, { FC } from 'react'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { CalendarIcon } from '@/atoms/Icons/CalendarIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { useDisclose } from '@/hooks/useDisclose'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { useLocale } from '@/utils/LocaleUtil'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { GradientWrapper } from '@/views/TicketsLandingView/components/GraidentWrapper'
import { ShareButton } from '@/views/TicketsLandingView/components/ShareButton'
import { useReleaseDateString } from '@/views/TicketsLandingView/hooks'
import { ComingSoonTheatricalRelease } from '@/views/TicketsLandingView/types'

interface Props {
  release: ComingSoonTheatricalRelease
  className?: string
}

export const ComingSoonReleaseCard: FC<Props> = ({ release, className }: Props) => {
  const { locale } = useLocale()
  const { t } = useTranslate('tickets')
  const { isOpen: isTrailerOpen, onClose: onTrailerClose, onOpen: openTrailer } = useDisclose(false)

  const { angelKeyArt16x9Image, trailerUrl, title, releaseDate, hubPageLink, description } = release

  const inTheatersLabel = useReleaseDateString(releaseDate, locale)

  return (
    <div className={classNames('w-full md:max-w-[320px] xl:max-w-[420px]', className)}>
      <GradientWrapper className="rounded-[14px]">
        <button
          onClick={openTrailer}
          className={classNames(
            'block aspect-w-16 aspect-h-9 rounded-[14px] w-full relative cursor-pointer border border-transparent overflow-hidden group focus-visible:border-black focus-visible:ring-[1px] focus-visible:ring-white',
            className,
          )}
        >
          <div>
            <Image
              className="transition-transform duration-[500ms] ease-in-out group-hover:scale-[1.02]"
              alt="Video player thumbnail"
              src={angelKeyArt16x9Image}
              fill
              sizes="(min-width: 1024px) 410px"
            />

            <div className="absolute inset-0 transition-colors duration-150 ease-in-out group-hover:bg-black/30 group-active:bg-black/50" />

            <div className="absolute left-1/2 top-1/2 flex h-[64px] w-[64px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 backdrop-blur-[48px] transition-all duration-150 ease-in-out group-hover:-translate-y-[calc(50%+2px)] group-hover:bg-white/50 group-focus-visible:-translate-y-[calc(50%+2px)] group-active:bg-white/20">
              <PlayIcon size={32} color="white" />
            </div>
          </div>
        </button>
      </GradientWrapper>

      <div className="mt-4 flex items-center gap-1 md:mt-6 md:gap-1.5">
        <CalendarIcon color="info-200" className="size-3 md:size-6" />
        <Text className="photon-caption-sm md:photon-caption-lg" color="info-200">
          {inTheatersLabel}
        </Text>
      </div>

      <Text weight="semibold" className="photon-title-sm md:photon-title-lg mt-3 block md:mt-4" color="white">
        {title}
      </Text>

      <div className="mt-4 flex flex-col gap-2 md:mt-6 md:flex-row">
        <div className="flex gap-2">
          <LinkButton size="sm" className="md:btn-md grow-1 md:grow-0" href={hubPageLink} variant="offwhite">
            {t('learnMore', 'Learn More')}
          </LinkButton>

          <ShareButton
            shareIconClassName="hidden"
            buttonSize="sm"
            className="!btn-sm md:btn-md grow-1 md:grow-0"
            textClassName="!block"
            title={t('comingSoonTheatricalTitleAngel', '{{projectName}} | Official Website | Angel', {
              projectName: title,
            })}
            message={description}
            shareUrl={getBasePath(locale) + hubPageLink}
          />
        </div>
      </div>

      {trailerUrl && (
        <YoutubeVideoModal handleClose={onTrailerClose} showModal={isTrailerOpen} youTubeUrl={trailerUrl} />
      )}
    </div>
  )
}
